// CoinGecko provider — live crypto spot prices + 24h change.
// Free public API, CORS-enabled, NO key required. An optional demo/pro key
// (VITE_COINGECKO_API_KEY) raises rate limits.

export interface Quote { price: number; chg: number; }

// Our asset symbol -> CoinGecko coin id
const COIN_IDS: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  SOL: 'solana',
};

const KEY = import.meta.env.VITE_COINGECKO_API_KEY as string | undefined;

export const CRYPTO_SYMBOLS = Object.keys(COIN_IDS);

/** Total crypto market cap (USD) and its 24h change, from /global. null on any failure. */
export async function fetchCryptoTotal(): Promise<{ cap: number; chg: number } | null> {
  const url = KEY ? 'https://pro-api.coingecko.com/api/v3/global' : 'https://api.coingecko.com/api/v3/global';
  const params = new URLSearchParams();
  if (KEY) params.set(KEY.startsWith('CG-') ? 'x_cg_pro_api_key' : 'x_cg_demo_api_key', KEY);
  try {
    const res = await fetch(KEY ? `${url}?${params.toString()}` : url, { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const d = (await res.json())?.data;
    const cap = d?.total_market_cap?.usd;
    const chg = d?.market_cap_change_percentage_24h_usd;
    return typeof cap === 'number' && cap > 0
      ? { cap, chg: typeof chg === 'number' ? +chg.toFixed(2) : 0 }
      : null;
  } catch { return null; }
}

/** Fetch live USD quotes for BTC/ETH/SOL. Returns {} on any failure. */
export async function fetchCryptoQuotes(): Promise<Record<string, Quote>> {
  const ids = Object.values(COIN_IDS).join(',');
  const base = KEY
    ? 'https://pro-api.coingecko.com/api/v3/simple/price'
    : 'https://api.coingecko.com/api/v3/simple/price';
  const params = new URLSearchParams({
    ids,
    vs_currencies: 'usd',
    include_24hr_change: 'true',
  });
  if (KEY) params.set(KEY.startsWith('CG-') ? 'x_cg_pro_api_key' : 'x_cg_demo_api_key', KEY);

  try {
    const res = await fetch(`${base}?${params.toString()}`, {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`coingecko ${res.status}`);
    const data = await res.json();
    const out: Record<string, Quote> = {};
    for (const [sym, id] of Object.entries(COIN_IDS)) {
      const row = data[id];
      if (row && typeof row.usd === 'number') {
        out[sym] = { price: row.usd, chg: +(row.usd_24h_change ?? 0).toFixed(2) };
      }
    }
    return out;
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[coingecko] live fetch failed, using simulation:', err);
    return {};
  }
}
